import FocusTrap from 'focus-trap-react';

import PropTypes from 'prop-types';

import React from 'react';
import { Link } from 'react-router';
import OnOffSwitch from '../../../components/on_off_switch.jsx';
import ZoomWidget from './reader_menu/zoom.jsx';

import { setShowOthersHighlights, setZoom } from '../../../actions/content';
import connect from '../../connect';
import { selectSection } from '../../../selectors/section';
import {
    selectShowOthersHighlights,
    selectZoomScale,
} from '../../../selectors/content';
import { isInstructorInSection } from '../../../selectors/current_user';
import { shouldComponentUpdate } from '../../../shallow_equal';
import { isConnected } from '../../../selectors/offline';

const setCanScroll = menuIsOpen =>
    document.body.classList[menuIsOpen ? 'add' : 'remove']('disable-scroll');

class ToggleNotes extends React.Component {
    static propTypes = {
        content: PropTypes.any,
        showOthersHighlights: PropTypes.bool.isRequired,
        setShowOthersHighlights: PropTypes.func.isRequired,
        isInstructor: PropTypes.bool.isRequired,
    };

    shouldComponentUpdate(nextProps, nextState) {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    toggle = () => {
        const {
            content,
            showOthersHighlights,
            setShowOthersHighlights,
        } = this.props;
        setShowOthersHighlights(content, !showOthersHighlights);
    };

    render() {
        const { showOthersHighlights, isInstructor } = this.props;

        const label = isInstructor ? 'Student questions' : 'Instructor notes';

        return (
            <span>
                <OnOffSwitch
                    className=""
                    checked={showOthersHighlights}
                    onChange={this.toggle}
                >
                    {label}
                </OnOffSwitch>
            </span>
        );
    }
}

const ReaderMenuPanel = function(props) {
    if (props.open) {
        return (
            <FocusTrap focusTrapOptions={{ onDeactivate: props.closeMenu }}>
                <div
                    onClick={props.closeMenu}
                    className="underlay"
                    style={ReaderMenuPanel.underlayStyle}
                />
                <ul
                    style={{ display: 'block', zIndex: 1060 }}
                    className="dropdown-menu"
                >
                    {React.Children.map(props.children, Child => (
                        <li>{Child}</li>
                    ))}
                </ul>
            </FocusTrap>
        );
    }

    // At least with React 0.13, returning null here causes React to replace
    // the `<noscript` placeholder every time.
    // TODO: Try null here when we upgrade to React 15
    return <span />;
};
// I know how much you love inline styles, so I copied these directly
// from react-aria-modal. Actually, we already know that code works in
// Engage, so there's no reason to make this complicated.
// https://github.com/davidtheclark/react-aria-modal/blob/ccd32947b35efbb4a48e138c5e9a7a0cd1c4c2e8/lib/Modal.js#L65-L76
ReaderMenuPanel.underlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1050,
    overflowX: 'hidden',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    textAlign: 'center',
};
ReaderMenuPanel.propTypes = {
    children: PropTypes.array.isRequired,
    open: PropTypes.bool,
    closeMenu: PropTypes.func.isRequired,
};

class ReaderMenu extends React.PureComponent {
    static propTypes = {
        content: PropTypes.any.isRequired,
        pagenum: PropTypes.number.isRequired,
        showOthersHighlights: PropTypes.bool.isRequired,
        setShowOthersHighlights: PropTypes.func.isRequired,
        zoomLevel: PropTypes.number.isRequired,
        setZoom: PropTypes.func.isRequired,
        isInstructor: PropTypes.bool.isRequired,
        isConnected: PropTypes.bool.isRequired,
        hideSharingQA: PropTypes.bool.isRequired,
    };

    constructor(props) {
        super(props);
        this.state = { open: false };
    }

    componentDidMount() {
        setCanScroll(false);
    }

    componentDidUpdate() {
        setCanScroll(this.state.open);
    }

    onEscape = e => {
        if (e.key === 'Escape') {
            this.closeMenu();
        }
    };

    closeMenu = () => {
        this.setState({ open: false });
    };

    handleDropdownButtonClicked = () => {
        this.setState({ open: !this.state.open });
    };

    render() {
        const {
            content,
            showOthersHighlights,
            setShowOthersHighlights,
        } = this.props;
        const {
            isInstructor,
            zoomLevel,
            setZoom,
            pagenum,
            isConnected,
            hideSharingQA,
        } = this.props;

        const menuItems = [];

        menuItems.push(
            <ZoomWidget
                key="zoom"
                content={content}
                zoomLevel={zoomLevel}
                setZoom={setZoom}
            />
        );
        if (!hideSharingQA) {
            menuItems.push(
                <ToggleNotes
                    key="notes"
                    content={content}
                    isInstructor={isInstructor}
                    showOthersHighlights={showOthersHighlights}
                    setShowOthersHighlights={setShowOthersHighlights}
                />
            );
        }

        if (!isInstructor && isConnected && !hideSharingQA) {
            menuItems.push(
                <Link
                    key="sharing"
                    onClick={this.closeMenu}
                    to={`content-${content.id}/address/${pagenum}/sharing`}
                    className="btn-sharing js-btn-sharing"
                >
                    <i className="cl-icon cl-icon-share" aria-hidden="true" />
                    Sharing
                </Link>
            );
        }

        if (isConnected) {
            menuItems.push(
                <Link
                    key="print"
                    onClick={this.closeMenu}
                    to={`content-${content.id}/address/${pagenum}/print`}
                >
                    <i
                        className="cl-icon cl-icon-print js-btn-print"
                        aria-hidden="true"
                    />
                    Print
                </Link>
            );
        }
        menuItems.push(
            <Link
                key="all_notes"
                onClick={this.closeMenu}
                to={`notes/content-${content.id}`}
            >
                <i className="cl-icon cl-icon-notes" aria-hidden="true" />
                View all notes
            </Link>
        );

        return (
            <span className="dropdown-wrapper" onKeyDown={this.onEscape}>
                <button
                    className="icon cl-icon cl-icon-more"
                    onClick={this.handleDropdownButtonClicked}
                >
                    <span className="aural">more</span>
                </button>
                <ReaderMenuPanel
                    open={this.state.open}
                    closeMenu={this.closeMenu}
                >
                    {menuItems}
                </ReaderMenuPanel>
            </span>
        );
    }
}

function mapStateToProps(state, props) {
    const { content } = props;

    const section = selectSection(state, content.section_id);

    return {
        zoomLevel: selectZoomScale(state, props.content),
        showOthersHighlights: !!selectShowOthersHighlights(state, content),
        isInstructor: isInstructorInSection(state, section),
        isConnected: isConnected(state),
    };
}

export default connect(
    mapStateToProps,
    {
        setShowOthersHighlights,
        setZoom,
    }
)(ReaderMenu);



// WEBPACK FOOTER //
// ./src/components/engage_reader/navbar/reader_menu.jsx